<template>
  <div class="about">
    <!-- 使用公共头部组件 -->
    <Header />
    
    <!-- 主要内容区域 -->
    <main class="main-content">
      <!-- 页面标题区域 -->
      <section class="page-header">
        <div class="container">
          <div class="header-content">
            <h1 class="page-title">关于我们</h1>
            <p class="page-subtitle">专业检测，值得信赖</p>
            <div class="breadcrumb">
              <span>首页</span>
              <el-icon><ArrowRight /></el-icon>
              <span>关于我们</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 公司介绍 -->
      <section class="company-intro">
        <div class="container">
          <div class="intro-content">
            <div class="intro-text">
              <div class="section-badge">
                <span>企业简介</span>
              </div>
              <h2>科学检测，服务民生</h2>
              <p class="intro-description">
                山东文达检测技术有限公司是一家专业从事第三方检测技术服务的高新技术企业，致力于为政府机构、企事业单位提供科学、公正、高效的检测解决方案。公司依托先进的检测设备和技术团队，业务涵盖水质检测、环境监测、食品安全、工业品检测等多个领域，助力客户实现质量管控与合规发展。
              </p>
              <p>
                <strong>技术实力</strong>：配备现代化实验室和精密仪器，严格遵循国家标准（如GB、ISO等）和行业规范，确保检测数据精准可靠。
              </p>
              <p>
                <strong>服务经验</strong>：多次参与政府招投标项目（如滕州市饮水安全水质检测），具备丰富的项目执行经验，服务周期灵活，响应迅速。
              </p>
              <p>
                <strong>专业团队</strong>：由资深技术专家和行业人才组成，提供从采样、检测到报告分析的全流程服务。
              </p>
              <p>
                <strong>社会责任</strong>：公司以"科学检测，服务民生"为宗旨，积极参与公共安全项目，如城乡饮用水质监测、环保督查等，为健康中国战略提供技术支持。
              </p>
              <p>
                <strong>发展愿景</strong>：未来，山东文达将持续拓展检测领域，深化技术创新，打造区域领先的检测服务品牌，为客户和社会创造更大价值。
              </p>
              <div class="intro-highlights">
                <div class="highlight-item">
                  <el-icon><Trophy /></el-icon>
                  <span>技术实力雄厚</span>
                </div>
                <div class="highlight-item">
                  <el-icon><Star /></el-icon>
                  <span>服务经验丰富</span>
                </div>
                <div class="highlight-item">
                  <el-icon><Medal /></el-icon>
                  <span>专业团队支撑</span>
                </div>
                <div class="highlight-item">
                  <el-icon><Document /></el-icon>
                  <span>CMA认证</span>
                </div>
              </div>
            </div>
            <div class="intro-visual">
              <div class="visual-container">
                <div class="visual-card card-1">
                  <div class="card-icon">
                    <el-icon><OfficeBuilding /></el-icon>
                  </div>
                  <div class="card-content">
                    <h4>现代化实验室</h4>
                    <p>配备国际先进检测设备</p>
                  </div>
                </div>
                <div class="visual-card card-2">
                  <div class="card-icon">
                    <el-icon><User /></el-icon>
                  </div>
                  <div class="card-content">
                    <h4>专业技术团队</h4>
                    <p>资深专家提供技术支持</p>
                  </div>
                </div>
                <div class="visual-card card-3">
                  <div class="card-icon">
                    <el-icon><Document /></el-icon>
                  </div>
                  <div class="card-content">
                    <h4>权威检测报告</h4>
                    <p>国际互认的检测结果</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 专业设备 -->
      <section class="equipment-section">
        <div class="container">
          <div class="section-header">
            <div class="section-badge">
              <span>专业设备</span>
            </div>
            <h2 class="section-title">先进检测设备</h2>
            <p class="section-subtitle">配备国际先进的检测仪器，确保检测结果精准可靠</p>
          </div>
          <div class="equipment-carousel">
            <div class="equipment-container">
              <div class="equipment-track">
                <div v-for="(image, index) in equipmentImages" :key="index" class="equipment-item">
                  <div class="equipment-image">
                    <img :src="image.src" :alt="image.alt" />
                  </div>
                  <div class="equipment-info">
                    <h3>{{ image.title }}</h3>
                    <p>{{ image.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 发展历程 -->
      <section class="timeline-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">发展历程</h2>
            <p class="section-subtitle">十五年专业积淀，见证行业发展</p>
          </div>
          <div class="timeline">
            <div class="timeline-item" v-for="(item, index) in timeline" :key="index">
              <div class="timeline-year">{{ item.year }}</div>
              <div class="timeline-content">
                <h3>{{ item.title }}</h3>
                <p>{{ item.description }}</p>
                <div class="timeline-achievements">
                  <span v-for="achievement in item.achievements" :key="achievement" class="achievement-tag">
                    {{ achievement }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 核心优势 -->
      <section class="advantages-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">核心优势</h2>
            <p class="section-subtitle">专业实力，值得信赖</p>
          </div>
          <div class="advantages-grid">
            <div class="advantage-card" v-for="(advantage, index) in advantages" :key="index">
              <div class="advantage-number">{{ String(index + 1).padStart(2, '0') }}</div>
              <div class="advantage-icon">
                <component :is="advantage.icon" />
              </div>
              <h3>{{ advantage.title }}</h3>
              <p>{{ advantage.description }}</p>
              <ul class="advantage-features">
                <li v-for="feature in advantage.features" :key="feature">{{ feature }}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <!-- 团队介绍 -->
      <section class="team-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">专业团队</h2>
            <p class="section-subtitle">汇聚行业精英，提供专业服务</p>
          </div>
          <div class="team-grid">
            <div class="team-member" v-for="(member, index) in teamMembers" :key="index">
              <div class="member-avatar">
                <div class="avatar-placeholder">
                  <el-icon><User /></el-icon>
                </div>
              </div>
              <div class="member-info">
                <h3>{{ member.name }}</h3>
                <p class="member-position">{{ member.position }}</p>
                <p class="member-description">{{ member.description }}</p>
                <div class="member-expertise">
                  <span v-for="skill in member.expertise" :key="skill" class="expertise-tag">
                    {{ skill }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 资质认证 -->
      <section class="certifications-section">
        <div class="container">
          <div class="section-header">
            <h2 class="section-title">资质认证</h2>
            <p class="section-subtitle">权威认证，品质保证</p>
          </div>
          <div class="certifications-grid">
            <div class="certification-card" v-for="(cert, index) in certifications" :key="index" @click="showCertification">
              <div class="cert-icon">
                <component :is="cert.icon" />
              </div>
              <h3>{{ cert.name }}</h3>
              <p>{{ cert.description }}</p>
              <div class="cert-details">
                <div class="cert-item">
                  <span class="label">证书编号：</span>
                  <span class="value">{{ cert.number }}</span>
                </div>
                <div class="cert-item">
                  <span class="label">有效期至：</span>
                  <span class="value">{{ cert.validity }}</span>
                </div>
              </div>
              <div class="cert-view-hint">
                <el-icon><Document /></el-icon>
                <span>点击查看认证证书</span>
              </div>
            </div>
          </div>

          <!-- 认证证书弹窗 -->
          <el-dialog
            v-model="showCertDialog"
            title="CMA认证证书"
            width="80%"
            :before-close="() => showCertDialog = false"
            center
          >
            <div class="cert-image-container">
              <img src="@/assets/img/zizhi.png" alt="CMA认证证书" class="cert-image" />
            </div>
          </el-dialog>
        </div>
      </section>

      <!-- 联系我们 -->
      <section class="contact-cta">
        <div class="container">
          <div class="cta-content">
            <h2>与我们合作</h2>
            <p>专业的检测服务，助力您的业务发展</p>
            <div class="cta-buttons">
              <el-button type="primary" size="large" @click="contactUs">
                <el-icon><Phone /></el-icon>
                立即咨询
              </el-button>
              <el-button size="large" @click="goToServices" class="secondary-btn">
                <el-icon><Service /></el-icon>
                查看服务
              </el-button>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 使用公共底部组件 -->
    <Footer />
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import { 
  ArrowRight,
  Trophy,
  Star,
  Medal,
  OfficeBuilding,
  User,
  Document,
  Phone,
  Service,
  DataAnalysis,
  Monitor,
  Setting,
  Shield,
  Timer,
  TrendCharts
} from '@element-plus/icons-vue'

export default {
  name: 'About',
  components: {
    Header,
    Footer,
    ArrowRight,
    Trophy,
    Star,
    Medal,
    OfficeBuilding,
    User,
    Document,
    Phone,
    Service,
    DataAnalysis,
    Monitor,
    Setting,
    Shield,
    Timer,
    TrendCharts
  },
  setup() {
    const router = useRouter()

    const timeline = ref([
      {
        year: '2008',
        title: '公司成立',
        description: '正式成立检测技术有限公司，开始专业检测服务',
        achievements: ['获得基础检测资质', '建立初期团队']
      },
      {
        year: '2012',
        title: '资质扩展',
        description: '获得CNAS认证，检测能力大幅提升',
        achievements: ['CNAS认证', '扩建实验室', '引进先进设备']
      },
      {
        year: '2016',
        title: '业务拓展',
        description: '服务领域扩展至多个行业，客户群体快速增长',
        achievements: ['多领域认证', '客户突破500家', '技术团队扩充']
      },
      {
        year: '2020',
        title: '数字化转型',
        description: '启动数字化转型，建立智能检测管理系统',
        achievements: ['数字化平台', '智能检测', '在线服务']
      },
      {
        year: '2024',
        title: '行业领先',
        description: '成为行业标杆企业，引领检测技术发展',
        achievements: ['行业领先地位', '技术创新奖', '服务千家企业']
      }
    ])

    const advantages = ref([
      {
        icon: 'DataAnalysis',
        title: '技术实力',
        description: '拥有行业领先的检测技术和设备',
        features: ['国际先进设备', '自主研发技术', '持续技术创新', '专业技术团队']
      },
      {
        icon: 'Shield',
        title: '质量保证',
        description: '严格的质量管理体系确保检测准确性',
        features: ['ISO质量体系', '全程质量控制', '多重检验机制', '结果可追溯']
      },
      {
        icon: 'Timer',
        title: '服务效率',
        description: '快速响应，高效完成检测任务',
        features: ['24小时响应', '标准化流程', '快速出具报告', '紧急检测服务']
      },
      {
        icon: 'TrendCharts',
        title: '行业经验',
        description: '十五年专业积淀，丰富的行业经验',
        features: ['多行业覆盖', '丰富案例经验', '专业解决方案', '持续服务优化']
      }
    ])

    const teamMembers = ref([
      {
        name: '张博士',
        position: '技术总监',
        description: '食品安全检测专家，拥有20年行业经验，主导多项国家标准制定',
        expertise: ['食品安全', '标准制定', '技术研发']
      },
      {
        name: '李工程师',
        position: '质量经理',
        description: '质量管理专家，负责实验室质量体系建设和持续改进',
        expertise: ['质量管理', '体系建设', 'CNAS认证']
      },
      {
        name: '王主任',
        position: '环境检测部主任',
        description: '环境监测领域资深专家，擅长复杂环境问题的检测分析',
        expertise: ['环境监测', '污染分析', '技术咨询']
      },
      {
        name: '陈经理',
        position: '客户服务经理',
        description: '客户服务专家，致力于为客户提供最优质的检测服务体验',
        expertise: ['客户服务', '项目管理', '解决方案']
      }
    ])

    const certifications = ref([
      {
        icon: 'Medal',
        name: 'CMA认证',
        description: '中国计量认证，具备法定检测资质',
        number: 'CMA 251520341741',
        validity: '2031年02月'
      }
    ])

    // 专业设备图片数据
    const equipmentImages = ref([
      {
        src: require('@/assets/shebeiimg/1.jpg'),
        alt: '专业检测设备1',
        title: '高精度分析仪器',
        description: '配备国际先进的高精度分析仪器，确保检测结果的准确性和可靠性'
      },
      {
        src: require('@/assets/shebeiimg/2.jpg'),
        alt: '专业检测设备2',
        title: '环境监测设备',
        description: '专业的环境监测设备，实时监控各项环境指标，保障环境安全'
      },
      {
        src: require('@/assets/shebeiimg/3.jpg'),
        alt: '专业检测设备3',
        title: '食品安全检测仪',
        description: '先进的食品安全检测仪器，全面保障食品质量和安全'
      },
      {
        src: require('@/assets/shebeiimg/4.jpg'),
        alt: '专业检测设备4',
        title: '材料性能测试机',
        description: '高精度材料性能测试设备，为各类材料提供专业检测服务'
      },
      {
        src: require('@/assets/shebeiimg/5.jpg'),
        alt: '专业检测设备5',
        title: '实验室分析系统',
        description: '现代化实验室分析系统，提供全方位的检测分析服务'
      }
    ])



    // 弹窗显示状态
    const showCertDialog = ref(false)

    // 显示认证图片弹窗
    const showCertification = () => {
      showCertDialog.value = true
    }

    const contactUs = () => {
      // 可以打开联系我们的弹窗或跳转到联系页面
      console.log('联系我们')
    }

    const goToServices = () => {
      router.push('/services')
    }

    return {
      timeline,
      advantages,
      teamMembers,
      certifications,
      equipmentImages,
      showCertDialog,
      showCertification,
      contactUs,
      goToServices
    }
  }
}
</script>

<style scoped>
.about {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  margin-top: 80px;
}

/* 页面标题区域 */
.page-header {
  background: 
    linear-gradient(135deg, rgba(30, 60, 114, 0.4) 0%, rgba(42, 82, 152, 0.4) 100%),
    url('@/assets/img/banner2.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  color: white;
  padding: 80px 0;
  position: relative;
  overflow: hidden;
  min-height: 400px;
}

/* 移动端背景适配：优化裁剪与空间 */
@media (max-width: 768px) {
  .page-header {
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    padding: 60px 0;
    min-height: max(320px, calc(100vw * 9 / 16));
  }
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><defs><pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse"><path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grid)"/></svg>');
  opacity: 0.3;
}

.header-content {
  text-align: center;
  position: relative;
  z-index: 1;
}

.page-title {
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 15px;
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 30px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  opacity: 0.8;
}

/* 通用样式 */
.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e3c72;
  margin-bottom: 20px;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #4facfe, #00f2fe);
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* 公司介绍 */
.company-intro {
  padding: 120px 0;
}

.intro-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.section-badge {
  display: inline-block;
  background: rgba(79, 172, 254, 0.1);
  color: #4facfe;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 25px;
}

.intro-text h2 {
  font-size: 2.2rem;
  font-weight: 700;
  color: #1e3c72;
  margin-bottom: 25px;
  line-height: 1.3;
}

.intro-description {
  font-size: 1.1rem;
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
}

.intro-text p {
  color: #666;
  line-height: 1.7;
  margin-bottom: 30px;
}

.intro-highlights {
  display: flex;
  gap: 30px;
  margin-top: 40px;
}

.highlight-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4facfe;
  font-weight: 600;
}

.highlight-item .el-icon {
  font-size: 1.2rem;
}

.visual-container {
  position: relative;
  height: 400px;
}

.visual-card {
  position: absolute;
  background: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  min-width: 280px;
  transition: all 0.3s ease;
}

.visual-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
}

.card-1 {
  top: 0;
  left: 0;
  z-index: 3;
}

.card-2 {
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  z-index: 2;
}

.card-3 {
  bottom: 0;
  left: 20%;
  z-index: 1;
}

.card-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.card-content h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e3c72;
  margin-bottom: 5px;
}

.card-content p {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 专业设备 */
.equipment-section {
  padding: 80px 0; /* 减小上下内边距 */
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%);
}

.equipment-carousel {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 80px; /* 增加左右内边距，为按钮留出空间 */
  position: relative;
}

.equipment-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 300px;
}

.equipment-track {
  display: flex;
  width: 100%; /* 一页显示5个设备，宽度为100% */
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

.equipment-item {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 280px;
  display: flex;
  flex-direction: column;
  width: calc(20% - 12px); /* 每个占20%减去间距，这样5个正好100% */
  flex-shrink: 0;
  margin: 0 6px;
}

.equipment-item:hover {
  transform: translateY(-8px);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.2);
}

.equipment-image {
  height: 180px; /* 减小图片高度 */
  overflow: hidden;
  position: relative;
}

.equipment-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.equipment-item:hover .equipment-image img {
  transform: scale(1.05); /* 减小缩放效果 */
}

.equipment-info {
  padding: 15px; /* 减小内边距 */
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
}

.equipment-info h3 {
  font-size: 1.1rem; /* 减小字体 */
  font-weight: 600; /* 减轻字重 */
  color: #1e3c72;
  margin-bottom: 8px; /* 减小间距 */
  line-height: 1.3;
  position: relative;
  padding-bottom: 6px; /* 减小内边距 */
}

.equipment-info h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 24px; /* 减小装饰线宽度 */
  height: 2px;
  background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 1px;
}

.equipment-info p {
  color: #666;
  line-height: 1.5; /* 减小行高 */
  font-size: 0.85rem; /* 减小字体 */
  margin: 0;
  text-align: center;
  max-width: 200px; /* 减小最大宽度 */
  margin: 0 auto;
}

/* 自定义轮播箭头 */
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.95);
  color: #4facfe;
  border: none;
  width: 45px; /* 减小尺寸 */
  height: 45px; /* 减小尺寸 */
  border-radius: 50%;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12); /* 减小阴影 */
  transition: all 0.3s ease;
  font-size: 16px; /* 减小字体 */
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-arrow:hover {
  background-color: #4facfe;
  color: white;
  transform: translateY(-50%) scale(1.1); /* 减小缩放 */
  box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3); /* 减小阴影 */
}

.carousel-arrow-left {
  left: 15px; /* 放在容器内侧，但不与设备重合 */
}

.carousel-arrow-right {
  right: 15px; /* 放在容器内侧，但不与设备重合 */
}

/* 自定义指示器 */
.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 6px; /* 减小间距 */
  margin-top: 20px; /* 减小上边距 */
}

.indicator {
  width: 32px; /* 减小宽度 */
  height: 5px; /* 减小高度 */
  background-color: rgba(79, 172, 254, 0.3);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background-color: #4facfe;
  width: 40px; /* 减小激活状态宽度 */
}

/* 发展历程 */
.timeline-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%);
}

.timeline {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #4facfe, #00f2fe);
  transform: translateX(-50%);
}

.timeline-item {
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  position: relative;
}

.timeline-item:nth-child(odd) {
  flex-direction: row;
}

.timeline-item:nth-child(even) {
  flex-direction: row-reverse;
}

.timeline-year {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
  position: relative;
  z-index: 2;
  box-shadow: 0 10px 30px rgba(79, 172, 254, 0.3);
}

.timeline-content {
  flex: 1;
  background: white;
  padding: 30px;
  border-radius: 15px;
  margin: 0 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
}

.timeline-item:nth-child(odd) .timeline-content::before {
  content: '';
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
  border-right: 15px solid white;
}

.timeline-item:nth-child(even) .timeline-content::before {
  content: '';
  position: absolute;
  right: -15px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 15px solid transparent;
  border-bottom: 15px solid transparent;
  border-left: 15px solid white;
}

.timeline-content h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1e3c72;
  margin-bottom: 10px;
}

.timeline-content p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
}

.timeline-achievements {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.achievement-tag {
  background: rgba(79, 172, 254, 0.1);
  color: #4facfe;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
}

/* 核心优势 */
.advantages-section {
  padding: 120px 0;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.advantage-card {
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  text-align: center;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid rgba(79, 172, 254, 0.1);
}

.advantage-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  border-color: #4facfe;
}

.advantage-number {
  position: absolute;
  top: -20px;
  right: 20px;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: white;
  font-size: 14px;
}

.advantage-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  font-size: 2rem;
  color: white;
}

.advantage-card h3 {
  font-size: 1.4rem;
  font-weight: 600;
  color: #1e3c72;
  margin-bottom: 15px;
}

.advantage-card p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 25px;
}

.advantage-features {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.advantage-features li {
  padding: 8px 0;
  color: #666;
  position: relative;
  padding-left: 20px;
  font-size: 14px;
}

.advantage-features li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #4facfe;
  font-weight: bold;
}

/* 团队介绍 */
.team-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%);
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.team-member {
  background: white;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.team-member:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.member-avatar {
  margin-bottom: 25px;
}

.avatar-placeholder {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 2.5rem;
  color: white;
}

.member-info h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1e3c72;
  margin-bottom: 5px;
}

.member-position {
  color: #4facfe;
  font-weight: 500;
  margin-bottom: 15px;
}

.member-description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 14px;
}

.member-expertise {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.expertise-tag {
  background: rgba(79, 172, 254, 0.1);
  color: #4facfe;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

/* 资质认证 */
.certifications-section {
  padding: 120px 0;
}

.certifications-grid {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
}

.certification-card {
  background: white;
  padding: 50px 40px;
  border-radius: 24px;
  text-align: center;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  border: 2px solid transparent;
  cursor: pointer;
  position: relative;
  width: 400px;
  max-width: 90vw;
}

.certification-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow: 0 25px 80px rgba(79, 172, 254, 0.2);
  border-color: #4facfe;
}

.cert-icon {
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  font-size: 2.5rem;
  color: white;
  box-shadow: 0 10px 30px rgba(79, 172, 254, 0.3);
}

.certification-card h3 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e3c72;
  margin-bottom: 20px;
  letter-spacing: 0.5px;
}

.certification-card p {
  color: #666;
  line-height: 1.7;
  margin-bottom: 30px;
  font-size: 1.1rem;
}

.cert-details {
  text-align: left;
  background: linear-gradient(135deg, #f8fbff 0%, #e8f4fd 100%);
  padding: 25px;
  border-radius: 16px;
  border: 1px solid rgba(79, 172, 254, 0.1);
}

.cert-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.cert-item:last-child {
  margin-bottom: 0;
}

.cert-item .label {
  color: #666;
  font-size: 14px;
}

.cert-item .value {
  color: #1e3c72;
  font-weight: 600;
  font-size: 14px;
}

.cert-view-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 25px;
  padding: 15px 20px;
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%);
  border-radius: 25px;
  color: #4facfe;
  font-size: 15px;
  font-weight: 600;
  transition: all 0.4s ease;
  border: 1px solid rgba(79, 172, 254, 0.2);
}

.cert-view-hint .el-icon {
  font-size: 18px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.certification-card:hover .cert-view-hint {
  background: linear-gradient(135deg, rgba(79, 172, 254, 0.2) 0%, rgba(0, 242, 254, 0.2) 100%);
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(79, 172, 254, 0.3);
}

/* 弹窗图片样式 */
.cert-image-container {
  text-align: center;
  padding: 20px;
}

.cert-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 联系CTA */
.contact-cta {
  padding: 100px 0;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  color: white;
  text-align: center;
}

.cta-content h2 {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
}

.cta-content p {
  font-size: 1.2rem;
  opacity: 0.9;
  margin-bottom: 40px;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  height: 50px;
  padding: 0 30px;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.secondary-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .intro-content {
    grid-template-columns: 1fr;
    gap: 60px;
  }
  
  .visual-container {
    height: 300px;
  }
  
  .visual-card {
    min-width: 250px;
    padding: 20px;
  }
  
  .intro-highlights {
    justify-content: center;
  }
  
  .equipment-carousel {
    padding: 0 40px;
    max-width: 900px;
  }

  .equipment-item {
    width: calc(33.33% - 10px); /* 平板显示3个设备 */
    height: 350px;
    margin: 0 5px;
  }

  .equipment-image {
    height: 200px;
  }

  .equipment-info {
    padding: 18px;
  }

  .equipment-info h3 {
    font-size: 1.1rem;
    margin-bottom: 8px;
  }

  .equipment-info p {
    font-size: 0.85rem;
    line-height: 1.4;
  }
}

/* 移动端设备垂直展示 */
@media (max-width: 768px) {
  .equipment-carousel {
    padding: 0 20px;
  }

  .equipment-container {
    overflow: visible;
    height: auto;
  }

  .equipment-track {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
    width: 100%;
    transform: none !important;
    transition: none;
  }

  .equipment-item {
    width: 100%;
    margin: 0;
    height: auto;
    min-height: 280px;
  }

  .equipment-image {
    height: 200px;
  }

  .equipment-info {
    padding: 20px;
  }

  .equipment-info h3 {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  .equipment-info p {
    font-size: 0.9rem;
    line-height: 1.5;
    display: block;
    -webkit-line-clamp: none;
    -webkit-box-orient: initial;
    overflow: visible;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-top: 70px;
  }
  
  .page-header {
    padding: 50px 0;
  }
  
  .page-title {
    font-size: 2.2rem;
    line-height: 1.2;
    margin-bottom: 15px;
  }
  
  .section-title {
    font-size: 1.8rem;
    line-height: 1.3;
    margin-bottom: 30px;
  }
  
  .company-intro,
  .equipment-section,
  .timeline-section,
  .advantages-section,
  .team-section,
  .certifications-section {
    padding: 60px 0;
  }

  /* 优化文本内容的字体和间距 */
  .intro-text {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  .card h3 {
    font-size: 1.2rem;
    margin-bottom: 10px;
  }

  .card p {
    font-size: 0.9rem;
    line-height: 1.5;
  }
  
  .timeline::before {
    left: 30px;
  }
  
  .timeline-item {
    flex-direction: row !important;
    padding-left: 80px;
  }
  
  .timeline-year {
    position: absolute;
    left: 0;
    width: 60px;
    height: 60px;
    font-size: 14px;
  }
  
  .timeline-content {
    margin: 0;
  }
  
  .timeline-content::before {
    left: -15px !important;
    border-right: 15px solid white !important;
    border-left: none !important;
  }
  
  .intro-highlights {
    flex-direction: column;
    gap: 15px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  /* 移动端按钮优化 */
  .btn {
    padding: 12px 24px;
    font-size: 1rem;
    min-height: 44px; /* 符合移动端触摸标准 */
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  .btn-primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    border: none;
    color: white;
  }

  .btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
  }

  /* 优化卡片交互 */
  .card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .card:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 15px;
  }
  
  .page-title {
    font-size: 1.8rem;
    line-height: 1.2;
    margin-bottom: 12px;
  }

  .page-header {
    padding: 40px 0;
  }

  .section-title {
    font-size: 1.6rem;
    line-height: 1.3;
    margin-bottom: 25px;
  }

  /* 进一步优化小屏文本 */
  .intro-text {
    font-size: 0.9rem;
    line-height: 1.5;
    margin-bottom: 18px;
  }

  .card h3 {
    font-size: 1.1rem;
    margin-bottom: 8px;
  }

  .card p {
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .company-intro,
  .equipment-section,
  .timeline-section,
  .advantages-section,
  .team-section,
  .certifications-section {
    padding: 50px 0;
  }

  /* 小屏手机按钮优化 */
  .btn {
    padding: 14px 28px;
    font-size: 1rem;
    min-height: 48px; /* 更大的触摸区域 */
    width: 100%;
    max-width: 280px;
    border-radius: 10px;
  }

  .cta-buttons {
    gap: 12px;
    width: 100%;
    padding: 0 20px;
  }

  /* 优化触摸交互 */
  .equipment-item:active {
    transform: scale(0.98);
  }

  .card:active {
    transform: scale(0.98);
  }

  /* 优化滚动体验 */
  .equipment-carousel {
    -webkit-overflow-scrolling: touch;
  }
  
  .advantages-grid,
  .team-grid,
  .certifications-grid {
    grid-template-columns: 1fr;
  }
  
  .visual-card {
    position: static !important;
    margin-bottom: 20px;
    transform: none !important;
  }
  
  .visual-container {
    height: auto;
  }

  /* 小屏手机设备垂直展示样式 */
  .equipment-carousel {
    padding: 0 15px;
  }

  .equipment-track {
    gap: 15px;
  }

  .equipment-item {
    width: 100%;
    height: auto;
    min-height: 260px;
    margin: 0;
  }

  .equipment-image {
    height: 180px;
  }

  .equipment-info {
    padding: 15px;
  }

  .equipment-info h3 {
    font-size: 1rem;
    margin-bottom: 8px;
    line-height: 1.3;
  }

  .equipment-info p {
    font-size: 0.85rem;
    line-height: 1.4;
    display: block;
    -webkit-line-clamp: none;
    -webkit-box-orient: initial;
    overflow: visible;
  }

  .equipment-section {
    padding: 60px 0;
  }

  .section-title {
    font-size: 1.8rem;
    margin-bottom: 30px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 375px) {
  .equipment-carousel {
    padding: 0 10px;
  }

  .equipment-track {
    gap: 12px;
  }

  .equipment-item {
    width: 100%;
    height: auto;
    min-height: 240px;
    margin: 0;
  }

  .equipment-image {
    height: 160px;
  }

  .equipment-info {
    padding: 8px;
  }

  .equipment-info h3 {
    font-size: 0.9rem;
    margin-bottom: 4px;
  }

  .equipment-info p {
    font-size: 0.7rem;
    line-height: 1.1;
  }

  .page-title {
    font-size: 1.8rem;
  }

  .section-title {
    font-size: 1.6rem;
    margin-bottom: 25px;
  }

  .container {
    padding: 0 10px;
  }

  /* 超小屏幕按钮优化 */
  .btn {
    padding: 12px 20px;
    font-size: 0.95rem;
    min-height: 44px;
    width: 100%;
    max-width: 260px;
  }

  .cta-buttons {
    gap: 10px;
    padding: 0 15px;
  }

  /* 优化触摸反馈 */
  .equipment-item {
    transition: transform 0.2s ease;
  }

  .equipment-item:active {
    transform: scale(0.96);
  }
}
</style>